import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Container, GridContainer } from './styles';
import FormDefault from '~/components/FormDefault';
import Loja from '~/components/Loja';
import { LojaContext } from '~/context/loja';
import { InputDate, InputSelect } from '~/components/NovosInputs';
import { useForm } from 'react-hook-form';
import ToggleDefault from '~/components/ToggleDefault';
import Separator from '~/components/Separator';
import api from '~/services/api';
import {
  GeracaoArqEscFiscalPayload,
  InterfaceSistemaProps,
  SelectType,
  ToggleProps,
} from './types';
import { tipoExportacao } from './utils/tipoExportacao';
import { BsFillLightningFill } from 'react-icons/bs';
import { useExpEscritaFiscal } from './hooks/useExpEscritaFiscal';
import { LogGrid } from './components/LogGrid';
import { Button } from './components/Button';
import { validaIntegridadeDataInicioFim } from '~/utils/functions';
import { toast } from 'react-toastify';
import { getMonth, parseISO } from 'date-fns';
import { checkToggleTrue } from './utils/checkToggleTrue';
import { escritaFiscalServices } from './services';

const ExportacaoEscritaFiscalContent: React.FC = () => {
  const { loja, changeLoja } = useContext(LojaContext);
  const {
    showGrid,
    handleShowGrid,
    disable,
    handleDisable,
    handleCodProcesso,
    codProcesso,
    refetchLogs,
    tipoStatus,
    logs,
    handleTipoStatus,
    resetLogs,
    handlePeriodo,
    handleEnableQuery,
    getDownload,
    urlToDownload,
    handleResetFlgArchiver,
  } = useExpEscritaFiscal();

  const [interfaceSistema, setInterfaceSistema] = useState<SelectType[]>([]);
  const [periodo, setPeriodo] = useState<SelectType>({
    value: -1,
    label: 'Selecione',
  });
  const [toggle, setToggle] = useState<ToggleProps>({
    somenteNaoExportados: false,
    marcarTodos: false,
    clientes: false,
    fornecedores: false,
    ecf: false,
  });

  const contentRef = useRef<HTMLDivElement | null>(null);

  const optionsPeriodo = [
    { label: 'Selecionar', value: -1 },
    { label: 'Mês Anterior', value: 1 },
    { label: 'Mês Atual', value: 2 },
  ];
  const {
    register,
    control,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
    watch,
    getValues,
    resetField,
    reset,
    setFocus,
  } = useForm({
    reValidateMode: 'onChange',
  });

  const dtaInicio = parseISO(watch('dta_inicio'));
  const dtaFim = parseISO(watch('dta_fim'));

  // Condição para data e período
  useEffect((): void => {
    const today = new Date();
    let year = today.getFullYear();
    const month = today.getMonth() + 1;
    let lastMonth = today.getMonth();

    if (periodo) {
      clearErrors('dta_inicio');
      clearErrors('dta_fim');
      // preencher data com mês anterior
      if (periodo.value === 1) {
        // caso esteja em janeiro, pegar mês anterior com base no ano anterior (dezembro)
        if (lastMonth === 0) {
          lastMonth = 12;
          year -= 1;
        }
        const dta_inicio = new Date(year, lastMonth - 1, 1);
        const dta_fim = new Date(year, lastMonth, 0);
        setValue('dta_inicio', dta_inicio.toLocaleDateString('en-CA'));
        setValue('dta_fim', dta_fim.toLocaleDateString('en-CA'));
        return;
      }

      // preencher data com mês atual
      if (periodo.value === 2) {
        const dta_inicio = new Date(year, month - 1, 1);
        const dta_fim = today;

        setValue('dta_inicio', dta_inicio.toLocaleDateString('en-CA'));
        setValue('dta_fim', dta_fim.toLocaleDateString('en-CA'));
        return;
      }
      setValue('dta_inicio', '');
      setValue('dta_fim', '');
    }
  }, [periodo]);

  useEffect(() => {
    if (tipoStatus >= 2 && logs.length > 0) {
      setValue('dta_inicio', '');
      setValue('dta_fim', '');

      setValue('tipo_exportacao', tipoExportacao[0]);
      setValue('cod_interface', interfaceSistema[0]);

      if (contentRef.current) {
        const seletorExportar: HTMLInputElement | null =
          contentRef.current.querySelector('[name="tipo_exportacao"]');
        if (seletorExportar) seletorExportar.value = '';
      }

      setToggle({
        somenteNaoExportados: false,
        marcarTodos: false,
        clientes: false,
        fornecedores: false,
        ecf: false,
      });
    }
  }, [getValues, interfaceSistema, logs, resetField, setValue, tipoStatus]);

  const watchInterfaceSistema = watch('cod_interface');
  const watchDtaInicio: string | undefined = watch('dta_inicio');
  const watchDtaFim: string | undefined = watch('dta_fim');

  const isInvalidPeriod = validaIntegridadeDataInicioFim(
    watchDtaInicio,
    watchDtaFim,
  );

  const isInterface322 = watchInterfaceSistema?.value === 322;

  const disableButtonGerarArquivo =
    !loja.cod_loja && watchInterfaceSistema === '';

  const getInterfaceSistema = useCallback(async () => {
    const { data } = await api.get('/interface-sistema');

    if (data.success) {
      const interfaces = data.data as InterfaceSistemaProps[];

      const i = interfaces.map(({ cod_interface, des_interface }) => ({
        label: des_interface,
        value: cod_interface,
      }));

      setInterfaceSistema(i);
      setValue('cod_interface', i[0]);
    }
  }, [setValue]);

  useEffect(() => {
    getInterfaceSistema();
  }, []);

  useEffect(() => {
    handleDisable('buttonGerarArquivo', disableButtonGerarArquivo);
  }, [disableButtonGerarArquivo, handleDisable]);

  useEffect(() => {
    if (
      toggle.marcarTodos &&
      (!toggle.clientes || !toggle.fornecedores || !toggle.ecf)
    ) {
      setToggle((prevToggle) => ({
        ...prevToggle,
        marcarTodos: false,
      }));
    }
  }, [toggle.clientes, toggle.fornecedores, toggle.ecf, toggle.marcarTodos]);

  useEffect(() => {
    setValue('tipo_exportacao', tipoExportacao[0]);
  }, [setValue]);

  const handleGerarArquivo = useCallback(
    async (e) => {
      e.preventDefault();
      if (disableButtonGerarArquivo)
        return toast.warning('Preencha os dados da tela para gerar o arquivo');

      if (isInvalidPeriod) {
        setError('dta_inicio', { type: 'value' });
        setError('dta_fim', { type: 'value' });
        return toast.warning('Verifique se as datas informadas são válidas');
      }
      handleResetFlgArchiver();

      clearErrors('dta_inicio');
      clearErrors('dta_fim');

      if (getMonth(dtaInicio) !== getMonth(dtaFim)) {
        setError('dta_inicio', { type: 'value' });
        setError('dta_fim', { type: 'value' });
        return toast.warning('Mês entre as datas devem ser idênticos');
      }

      clearErrors('dta_inicio');
      clearErrors('dta_fim');

      // Validação dos toggles - apenas se não for interface 322
      if (!isInterface322 && !checkToggleTrue(toggle))
        return toast.warning(
          'Ao menos uma tabela deve ter sido selecionada (Cliente, Fornecedor ou ECF)',
        );

      if (tipoStatus >= 2 && logs.length >= 3) {
        handleTipoStatus(0);
        resetLogs();
      }

      handlePeriodo(dtaInicio, dtaFim);

      setTimeout(() => {
        handleEnableQuery(true);
      }, 2800);

      const codInterface: number = getValues('cod_interface')?.value;
      const tpExportacao: number = getValues('tipo_exportacao')?.value;

      const geracaoPayload: GeracaoArqEscFiscalPayload = {
        cod_loja: loja.cod_loja,
        cod_interface: codInterface ?? 6,
        dta_inicio: dtaInicio,
        dta_fim: dtaFim,
        tipo_exportacao: isInterface322 ? 1 : tpExportacao ?? 1, // Valor padrão para interface 322
        flg_som_n_exportado: isInterface322
          ? false
          : toggle.somenteNaoExportados,
        flg_ger_cliente: isInterface322 ? true : toggle.clientes,
        flg_ger_fornecedor: isInterface322 ? true : toggle.fornecedores,
        flg_ger_ecf: isInterface322 ? true : toggle.ecf,
      };

      const responseEscritaFiscal = await escritaFiscalServices.storeProcesso(
        geracaoPayload,
      );

      if (responseEscritaFiscal.success) {
        handleCodProcesso(responseEscritaFiscal.data.cod_processo);

        handleDisable('form', true);
        handleDisable('buttonGerarArquivo', true);
        handleShowGrid(true);
      }
    },
    [
      clearErrors,
      disableButtonGerarArquivo,
      getValues,
      handleCodProcesso,
      handleDisable,
      handleEnableQuery,
      handlePeriodo,
      handleResetFlgArchiver,
      handleShowGrid,
      handleTipoStatus,
      isInterface322,
      isInvalidPeriod,
      logs,
      loja.cod_loja,
      resetLogs,
      setError,
      tipoStatus,
      toggle,
      dtaInicio,
      dtaFim,
    ],
  );

  const handleDownload = useCallback(async () => {
    if (tipoStatus >= 2 && codProcesso) {
      await getDownload({ isButtonClick: true });
    }
  }, [codProcesso, getDownload, tipoStatus]);

  const handleInterfaceChange = useCallback(
    (selected: any) => {
      reset();
      setToggle((prev) => ({
        ...prev,
        marcarTodos: false,
        clientes: false,
        fornecedores: false,
        ecf: false,
        somenteNaoExportados: false,
      }));
      setValue('cod_interface', selected);
    },
    [reset, setValue],
  );

  const tipoExportacaoDinamico = useMemo<SelectType[]>(() => {
    if (!watchDtaInicio) {
      return tipoExportacao.filter((item) => item.value !== 3);
    }

    const month = getMonth(parseISO(watchDtaInicio)) + 1;

    // Fevereiro → mostra inventário
    if (month === 2) {
      return tipoExportacao.map((item) =>
        item.value === 0
          ? { ...item, label: 'Entradas, Saídas e Inventário' }
          : item,
      );
    }

    // Outros meses → remove inventário
    return tipoExportacao
      .filter((item) => item.value !== 3)
      .map((item) =>
        item.value === 0 ? { ...item, label: 'Entradas e Saídas' } : item,
      );
  }, [watchDtaInicio]);

  useEffect(() => {
    const selected = getValues('tipo_exportacao');

    if (!selected) return;

    const existsInOptions = tipoExportacaoDinamico.some(
      (opt) => opt.value === selected.value,
    );

    if (!existsInOptions) {
      setValue('tipo_exportacao', undefined);
    }
  }, [tipoExportacaoDinamico, getValues, setValue]);

  return (
    <Container>
      <FormDefault
        codTela={284}
        title="Exportação de Escrita Fiscal"
        onSave={() => Promise.resolve()}
        onCancel={() => []}
        onClearFields={() => []}
        onNew={() => []}
        onDelete={() => []}
        onReturnSearch={() => []}
        isDelete={false}
        isClear={false}
        isSave
        isNew={false}
        isCancel={false}
        customButtons={
          <Button.Root
            width={300}
            height={40}
            onClick={handleGerarArquivo}
            disabled={disable.buttonGerarArquivo}
          >
            <BsFillLightningFill fontSize="1.25rem" color="#fff" />
            GERAR ARQUIVO
          </Button.Root>
        }
      >
        <div className="contentHeader">
          <div className="contentHeaderLoja">
            <Loja
              selectedLoja={loja.cod_loja}
              IsInvalid={false}
              onChange={(e) => {
                changeLoja(typeof e === 'number' ? e : loja.cod_loja);
              }}
              isDisabled={disable.form}
            />
          </div>
          <InputSelect
            label="Interface Sistema"
            placeholder="Selecione..."
            name="cod_interface"
            register={register}
            isError={!!errors.cod_interface}
            control={control}
            disabled={disable.form}
            options={interfaceSistema}
            changeSelected={(selected) => {
              handleInterfaceChange(selected);
            }}
            style={{ width: '18.75rem' }}
          />
        </div>
        <div ref={contentRef} className="content">
          <div>
            <InputSelect
              label="Período"
              placeholder="Selecione"
              name="periodo"
              register={register}
              isError={!!errors.periodo}
              control={control}
              disabled={disable.buttonGerarArquivo}
              options={optionsPeriodo}
              autoFocus
              changeSelected={(selected) => {
                clearErrors('periodo');
                setPeriodo(selected);
                setValue('periodo', selected);
                if (selected.value === -1) {
                  setTimeout(() => {
                    setFocus('dta_inicio');
                  }, 50);
                }
              }}
            />
          </div>
          <div className="contentDate">
            <InputDate
              id="dta"
              label="Início:"
              name="dta_inicio"
              register={register}
              control={control}
              disabled={
                disable.form || periodo.value === 1 || periodo.value === 2
              }
              isError={!!errors.dta_inicio}
            />
            <InputDate
              id="dta"
              label="Término:"
              name="dta_fim"
              register={register}
              control={control}
              disabled={
                disable.form || periodo.value === 1 || periodo.value === 2
              }
              isError={!!errors.dta_fim}
            />
          </div>
          <div className="contentExportar">
            <InputSelect
              label="Exportar"
              placeholder="Selecione..."
              name="tipo_exportacao"
              register={register}
              isError={!!errors.tipo_exportacao}
              control={control}
              disabled={disable.form || isInterface322}
              options={tipoExportacaoDinamico}
              changeSelected={(selected) => {
                setValue('tipo_exportacao', selected);
              }}
              style={{ width: '18.75rem' }}
            />
            <ToggleDefault
              labelText="Somente não exportados?"
              setChecked={toggle.somenteNaoExportados}
              onSwitch={() => {
                setToggle((prev) => ({
                  ...prev,
                  somenteNaoExportados: !toggle.somenteNaoExportados,
                }));
              }}
              size="large"
              disabled={disable.form || isInterface322}
            />
          </div>
        </div>

        <Separator labelText="Gerar" childrenWidth="8.125rem">
          <ToggleDefault
            labelText="Marcar todos?"
            labelOnRight
            inLine
            setChecked={
              toggle.marcarTodos ||
              (toggle.clientes && toggle.fornecedores && toggle.ecf)
            }
            onSwitch={() => {
              setToggle((prev) => ({
                ...prev,
                marcarTodos: !toggle.marcarTodos,
                clientes: !toggle.marcarTodos,
                fornecedores: !toggle.marcarTodos,
                ecf: !toggle.marcarTodos,
              }));
            }}
            size="large"
            disabled={disable.form || isInterface322}
          />
        </Separator>
        <div className="toggles">
          <ToggleDefault
            labelText="Clientes"
            inLine
            setChecked={toggle.clientes}
            onSwitch={() => {
              setToggle((prev) => ({
                ...prev,
                clientes: !toggle.clientes,
              }));
            }}
            size="large"
            disabled={disable.form || isInterface322}
          />
          <ToggleDefault
            labelText="Fornecedores"
            inLine
            setChecked={toggle.fornecedores}
            onSwitch={() => {
              setToggle((prev) => ({
                ...prev,
                fornecedores: !toggle.fornecedores,
              }));
            }}
            size="large"
            disabled={disable.form || isInterface322}
          />
          <ToggleDefault
            labelText="ECF"
            inLine
            setChecked={toggle.ecf}
            onSwitch={() => {
              setToggle((prev) => ({
                ...prev,
                ecf: !toggle.ecf,
              }));
            }}
            size="large"
            disabled={disable.form || isInterface322}
          />
        </div>
        {showGrid && (
          <GridContainer>
            <Separator
              labelText={`Logs da execução do processo #${codProcesso ?? 0}`}
              sidePadding="0 0"
            />
            <LogGrid />
            <div className="buttons">
              <Button.Root
                disabled={tipoStatus < 2}
                width={170}
                bgColor="GREEN"
                onClick={handleDownload}
              >
                {tipoStatus < 2 ? 'Gerando arquivo...' : 'Baixar arquivo'}
              </Button.Root>
              <Button.Root
                disabled={tipoStatus >= 2}
                onClick={() => refetchLogs()}
                width={120}
              >
                Atualizar
              </Button.Root>
            </div>
          </GridContainer>
        )}
      </FormDefault>
    </Container>
  );
};

export default ExportacaoEscritaFiscalContent;
